import styled from 'styled-components'
import { rem } from 'polished'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Flag from 'LuxKit/Flag'
import Modal from 'LuxKit/Modal/Modal'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { getCountryCodeFromLocale, getNativeNameForLocale } from 'i18n/util/locale'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'

const LanguageWarningLabel = styled.span`
  display: inline-flex;
  gap: ${rem(8)};
  align-items: center;
`

const continueInLanguageLabelByLocale: Record<string, string> = {
  'ar-AE': 'المتابعة باللغة العربية',
  'ar-BH': 'المتابعة باللغة العربية',
  'ar-EG': 'المتابعة باللغة العربية',
  'ar-JO': 'المتابعة باللغة العربية',
  'ar-KW': 'المتابعة باللغة العربية',
  'ar-MA': 'المتابعة باللغة العربية',
  'ar-OM': 'المتابعة باللغة العربية',
  'ar-QA': 'المتابعة باللغة العربية',
  'ar-SA': 'المتابعة باللغة العربية',
  'bg-BG': 'Продължи на български',
  'bn-BD': 'বাংলায় চালিয়ে যান',
  'ca-ES': 'Continua en català',
  'cs-CZ': 'Pokračovat v češtině',
  'da-DK': 'Fortsæt på dansk',
  'de-DE': 'Auf Deutsch fortfahren',
  'el-GR': 'Συνέχεια στα Ελληνικά',
  'en-AU': 'Continue in English',
  'en-GB': 'Continue in English',
  'en-US': 'Continue in English',
  'es-ES': 'Continuar en español',
  'es-MX': 'Continuar en español',
  'et-EE': 'Jätka eesti keeles',
  'fa-IR': 'ادامه به فارسی',
  'fi-FI': 'Jatka suomeksi',
  'fil-PH': 'Magpatuloy sa Filipino',
  'fr-FR': 'Continuer en français',
  'gu-IN': 'ગુજરાતીમાં ચાલુ રાખો',
  'he-IL': 'המשך בעברית',
  'hi-IN': 'हिंदी में जारी रखें',
  'hr-HR': 'Nastavi na hrvatskom',
  'hu-HU': 'Folytatás magyarul',
  'id-ID': 'Lanjutkan dalam Bahasa Indonesia',
  'it-IT': 'Continua in italiano',
  'ja-JP': '日本語で続ける',
  'kn-IN': 'ಕನ್ನಡದಲ್ಲಿ ಮುಂದುವರಿಸಿ',
  'ko-KR': '한국어로 계속하기',
  'lt-LT': 'Tęsti lietuvių kalba',
  'lv-LV': 'Turpināt latviski',
  'ml-IN': 'മലയാളത്തിൽ തുടരുക',
  'mr-IN': 'मराठीत सुरू ठेवा',
  'ms-MY': 'Teruskan dalam Bahasa Melayu',
  'nl-NL': 'Verdergaan in het Nederlands',
  'no-NO': 'Fortsett på norsk',
  'pa-IN': 'ਪੰਜਾਬੀ ਵਿੱਚ ਜਾਰੀ ਰੱਖੋ',
  'pl-PL': 'Kontynuuj po polsku',
  'pt-BR': 'Continuar em português',
  'pt-PT': 'Continuar em português',
  'ro-RO': 'Continuă în română',
  'ru-RU': 'Продолжить на русском',
  'sk-SK': 'Pokračovať v slovenčine',
  'sl-SI': 'Nadaljuj v slovenščini',
  'sr-RS': 'Настави на српском',
  'sw-KE': 'Endelea kwa Kiswahili',
  'sv-SE': 'Fortsätt på svenska',
  'ta-IN': 'தமிழில் தொடரவும்',
  'ta-LK': 'தமிழில் தொடரவும்',
  'te-IN': 'తెలుగులో కొనసాగండి',
  'th-TH': 'ดำเนินการต่อเป็นภาษาไทย',
  'tr-TR': 'Türkçe devam et',
  'uk-UA': 'Продовжити українською',
  'ur-PK': 'اردو میں جاری رکھیں',
  'vi-VN': 'Tiếp tục bằng tiếng Việt',
  'zh-CN': '继续使用简体中文',
  'zh-HK': '繼續使用繁體中文',
  'zh-TW': '繼續使用繁體中文',
}

function LocaleRedirectModal({ accountLocale, urlLocale, onKeepPageLanguage, onSwitchToAccountLanguage }: {
  accountLocale: string
  urlLocale: string
  onKeepPageLanguage: () => Promise<void> | void
  onSwitchToAccountLanguage: () => Promise<void> | void
}) {
  const [loading, setLoading] = useState(false)

  const intl = useIntl()
  const modalContext = useModalElementContext<'url' | 'auth'>()
  const { languageOptions } = useLocaleUrlOptions()
  const onClose = useCallback(() => modalContext.resolve('url'), [modalContext])

  const urlLanguage = getNativeNameForLocale(urlLocale, languageOptions) ?? urlLocale
  const accountLanguage = getNativeNameForLocale(accountLocale, languageOptions) ?? accountLocale

  const urlCountry = getCountryCodeFromLocale(urlLocale, languageOptions)
  const accountCountry = getCountryCodeFromLocale(accountLocale, languageOptions)

  return (
    <Modal
      title={intl.formatMessage({
        id: 'jVH8voIfhVh0RZcbF60E',
        defaultMessage: 'Choose your language',
      })}
      loading={loading}
      isPrimaryActionDisabled={loading}
      isSecondaryActionDisabled={loading}
      primaryActionText={
        <LanguageWarningLabel>
          {accountCountry ? <Flag countryCode={accountCountry} aria-hidden /> : null}
          {continueInLanguageLabelByLocale[accountLocale]}
        </LanguageWarningLabel>
      }
      secondaryActionText={
        <LanguageWarningLabel>
          {urlCountry ? <Flag countryCode={urlCountry} aria-hidden /> : null}
          {intl.formatMessage({
            id: 'mvniesvnqwYRAJMKgWqJ',
            defaultMessage: 'Keep page language',
          })}
        </LanguageWarningLabel>
      }
      onPrimaryActionClick={() => {
        setLoading(true)
        onSwitchToAccountLanguage()
      }}
      onSecondaryActionClick={async() => {
        setLoading(true)
        await onKeepPageLanguage()
        setLoading(false)
      }}
      onClose={onClose}
    >
      <BodyText variant="large">
        {intl.formatMessage(
          {
            id: '4Z4zmNyyNhN2PC0qkMZ9',
            defaultMessage:
              'This page is in {urlLanguage}, but your account language is {authLanguage}.',
          },
          { urlLanguage, authLanguage: accountLanguage },
        )}
      </BodyText>
    </Modal>
  )
}

export default LocaleRedirectModal
